import PageHeaderActions from "Components/Common/PageHeaderActions";
import { StatsCardProps } from "Components/Common/StatsCard";
import StatsGroup from "Components/Common/StatsGroup";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { IconName } from "Components/Icons/Icon";
import InvoiceList from "Components/Invoices/InvoiceList";
import RestrictedAccessModal from "Components/Modals/RestrictedAccessModal";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "reactstrap";
import {
  clearInvoiceDetails,
  resetCreateInvoiceStatus,
  resetEditInvoiceStatus,
} from "slices/invoices/reducer";
import { clearItemState } from "slices/items/reducer";
import { clearQuoteDetails } from "slices/quotes/reducer";
import { tourElementsIds } from "tour/tourElementsIds";
import { useScreenTour } from "tour/useScreenTour";
import { ErrorToast } from "utils/toast";

const Invoices = () => {
  const [showSubscriptionExpiredPopUp, setShowSubscriptionExpiredPopUp] =
    useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);

  const hasNotSubscribed =
    user?.is_subscription_active === false && user.is_trial_period === false;
  const hasBankAccount = user?.hasBankAccountDetailAdded;
  const hasSignature = user?.hasSignatureAdded;
  const [restrictionModalData, setRestrictionModalData] = useState<{
    iconName: IconName;
    title: string;
    description: string;
    buttonText: string;
    onAction: () => void;
  }>({
    iconName: "" as IconName,
    title: "",
    description: "",
    buttonText: "",
    onAction: () => {},
  });

  const RESTRICTION_CONFIG = {
    SUBSCRIPTION_REQUIRED: {
      iconName: "SubscriptionRequired",
      title: "Subscription Required",
      description:
        "To continue using EaziQuote, please activate or renew your subscription.",
      buttonText: "Activate Subscription",
      onAction: () => {
        if (user?.is_team_member) {
          ErrorToast(
            "Only the account owner can manage subscriptions. Please contact your owner."
          );
        } else {
          navigate("/subscribe-plan");
        }
      },
    },
    BANK_ACCOUNT_REQUIRED: {
      iconName: "Info",
      title: "Add Bank Details",
      description:
        "Including bank details makes it easier for clients to pay you without follow-ups.",
      buttonText: "Add Bank Details",
      onAction: () => navigate("/payments-and-invoicing"),
    },
    SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Add Your Signature",
      description:
        "Adding a signature helps build trust and makes your quote feel complete and professional.",
      buttonText: "Add Signature",
      onAction: () =>
        navigate("/payments-and-invoicing", {
          state: {
            signatureMissing: true,
          },
        }),
    },
    BANK_AND_SIGNATURE_REQUIRED: {
      iconName: "Info",
      title: "Complete Your Setup",
      description:
        "Add your bank details and signature to ensure your quotes look professional and include payment information.",
      buttonText: "Complete Setup",
      onAction: () => navigate("/payments-and-invoicing"),
    },
  } as const;

  const invoiceTour = useScreenTour("invoice");

  document.title = "Invoices | EaziQuote";

  const summary = useAppSelector((s) => s.invoice.summary);

  const invoiceStatsData: StatsCardProps[] = [
    {
      title: "TOTAL INVOICES",
      icon: "BigBlueReceipt",
      value: summary?.total_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "PAID",
      icon: "BigGreenTickMark",
      value: summary?.paid_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "OUTSTANDING",
      icon: "BigOrangeWatch",
      value: summary?.outstanding_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
    {
      title: "OVERDUE",
      icon: "BigRedSandWatch",
      value: summary?.overdue_count || 0,
      suffix: "",
      prefix: "",
      separator: ",",
      decimal: 0,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid>
        <div className="gap-3 d-flex flex-column">
          <PageHeaderActions
            title="Invoices"
            subtitle="Manage all your invoices in one place"
            id={tourElementsIds.invoiceTour}
            actions={[
              {
                label: "New Invoice",
                leftIcon: <i className="ri-add-fill me-1 align-bottom"></i>,
                onClick: () => {
                  if (hasNotSubscribed) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.SUBSCRIPTION_REQUIRED
                    );
                  } else if (!hasBankAccount && !hasSignature) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.BANK_AND_SIGNATURE_REQUIRED
                    );
                  } else if (!hasBankAccount) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.BANK_ACCOUNT_REQUIRED
                    );
                  } else if (!hasSignature) {
                    setShowSubscriptionExpiredPopUp(true);
                    setRestrictionModalData(
                      RESTRICTION_CONFIG.SIGNATURE_REQUIRED
                    );
                  } else {
                    dispatch(clearQuoteDetails());
                    dispatch(clearInvoiceDetails());
                    dispatch(resetEditInvoiceStatus());
                    dispatch(resetCreateInvoiceStatus());
                    dispatch(clearItemState());
                    navigate("/manage-invoice");
                  }
                },
              },
            ]}
          />
          <StatsGroup items={invoiceStatsData} />
          <InvoiceList />
        </div>
        <OverlayGuide {...invoiceTour} />
        <RestrictedAccessModal
          isOpen={showSubscriptionExpiredPopUp}
          onCancel={() => setShowSubscriptionExpiredPopUp(false)}
          iconName={restrictionModalData.iconName}
          title={restrictionModalData.title}
          description={restrictionModalData.description}
          buttonText={restrictionModalData.buttonText}
          onButtonClick={restrictionModalData.onAction}
        />
      </Container>
    </div>
  );
};

export default Invoices;
